import { useEffect, useState, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { getFleetHealth } from '../api/tankguard'
import { timeAgo } from '../utils/events'
import { isUnconfigured } from '../utils/device'
import { useSettings } from '../api/settings'

function StarBar({ q }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            color: i < q ? '#f59e0b' : 'var(--tg-graphite-300, #d4d4d4)',
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          ★
        </span>
      ))}
    </span>
  )
}

function GsmBars({ value }) {
  // gsm_signal is 0-5 on the FMB920. Render 5 bars with the active count filled.
  const v = Math.max(0, Math.min(5, value | 0))
  const color = v >= 3 ? '#16a34a' : v >= 1 ? '#f59e0b' : '#dc2626'
  return (
    <span style={{ display: 'inline-flex', gap: 2, alignItems: 'flex-end', height: 14 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          style={{
            width: 3,
            height: 3 + i * 2,
            background: i <= v ? color : 'var(--tg-graphite-300, #d4d4d4)',
            borderRadius: 1,
          }}
        />
      ))}
    </span>
  )
}

function VoltageGauge({ mv }) {
  if (!mv) return <span style={{ color: 'var(--tg-text-muted)' }}>—</span>
  const v = mv / 1000
  // 12V truck batteries: 12.5+ healthy, 11.5-12.5 mediocre, <11.5 low.
  const color = v >= 12.5 ? '#16a34a' : v >= 11.5 ? '#f59e0b' : '#dc2626'
  return (
    <span style={{ color, fontWeight: 600 }}>
      {v.toFixed(2)}V
    </span>
  )
}

const SEVERITY_COLORS = {
  critical: { fg: 'var(--tg-danger-700)', bg: 'var(--tg-danger-50)', border: 'var(--tg-danger-100, #fecaca)' },
  high:     { fg: 'var(--tg-danger-700)', bg: 'var(--tg-danger-50)', border: 'var(--tg-danger-100, #fecaca)' },
  medium:   { fg: '#92400e',              bg: 'var(--tg-warning-50, #fef3c7)', border: '#fde68a' },
}

const STATUS_PILL = {
  ok:       { label: 'OK',       fg: 'var(--tg-success-700, #047857)', bg: 'var(--tg-success-50, #ecfdf5)' },
  warning:  { label: 'Warning',  fg: '#92400e',                         bg: 'var(--tg-warning-50, #fef3c7)' },
  critical: { label: 'Critical', fg: 'var(--tg-danger-700)',           bg: 'var(--tg-danger-50)' },
  // No real hardware assigned — neutral, never an alert colour.
  none:     { label: 'No device', fg: 'var(--tg-text-muted)',          bg: 'var(--tg-bg-subtle)' },
}

export default function SensorHealth() {
  const settings = useSettings()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    try {
      const r = await getFleetHealth()
      setData(r)
      setErr('')
    } catch (e) {
      console.error(e); Sentry.captureException(e)
      setErr(e.message || 'Failed to load sensor health')
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, settings.refreshMs)
    return () => clearInterval(t)
  }, [load, settings.refreshMs])

  if (loading && !data) {
    return <div className="page"><div className="tg-card" style={{ padding: 20, color: 'var(--tg-text-muted)' }}>Loading sensor health…</div></div>
  }
  if (err) {
    return <div className="page"><div className="tg-card" style={{ padding: 20, color: 'var(--tg-danger-700)' }}>{err}</div></div>
  }

  const total = data?.total_trucks || 0
  const ok = data?.trucks_ok || 0
  const alerted = data?.trucks_with_alerts || 0
  const pctOk = total > 0 ? Math.round((ok / total) * 100) : 100

  return (
    <div className="page">
      {/* Summary header */}
      <div className="tg-card" style={{ padding: 'var(--tg-space-4)', marginBottom: 'var(--tg-space-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 700, color: pctOk >= 80 ? 'var(--tg-success-700, #047857)' : pctOk >= 50 ? '#92400e' : 'var(--tg-danger-700)' }}>
              {ok} / {total}
            </div>
            <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Trucks healthy
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 240, height: 8, background: 'var(--tg-bg-subtle)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: pctOk + '%', height: '100%', background: pctOk >= 80 ? 'var(--tg-success-500, #10b981)' : pctOk >= 50 ? '#f59e0b' : 'var(--tg-danger-500)' }} />
          </div>
          {alerted > 0 && (
            <div style={{ fontSize: 12, color: 'var(--tg-danger-700)', fontWeight: 600 }}>
              {alerted} truck{alerted !== 1 ? 's' : ''} need attention
            </div>
          )}
        </div>
      </div>

      {/* Truck cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--tg-space-4)' }}>
        {(data?.trucks || []).map(t => {
          const sh = t.sensor_health || {}
          // No real IMEI (or never connected) → neutral "No device" card, never
          // green/online and never an OK/CRITICAL alert badge driven by the stale
          // telemetry a leftover ThingsBoard device id may still be leaking.
          const noDevice = isUnconfigured(t)
          const pill = noDevice ? STATUS_PILL.none : (STATUS_PILL[t.status] || STATUS_PILL.ok)
          const statusLabel = noDevice
            ? (t.connectivity === 'never_connected' ? 'Never connected' : 'No device')
            : (t.online ? 'Online' : 'Offline')
          return (
            <div key={t.id} className="tg-card" style={{ padding: 'var(--tg-space-4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: noDevice ? 'var(--tg-text-muted)' : 'var(--tg-text-primary)' }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: (noDevice || !t.online) ? 'var(--tg-graphite-300)' : 'var(--tg-status-online)' }} />
                    {statusLabel}
                    {!noDevice && t.last_seen ? ' · ' + timeAgo(t.last_seen) : ''}
                  </div>
                </div>
                <span style={{ background: pill.bg, color: pill.fg, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '3px 8px', borderRadius: 4 }}>
                  {pill.label}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12, padding: '10px 12px', background: 'var(--tg-bg-subtle)', borderRadius: 6, opacity: noDevice ? 0.45 : 1 }}>
                <div>
                  <div style={{ fontSize: 9, color: 'var(--tg-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>BLE quality</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <StarBar q={sh.ble_quality || 0} />
                    <span style={{ fontSize: 11, color: sh.ble_ok ? 'var(--tg-success-700, #047857)' : 'var(--tg-danger-700)' }}>
                      {sh.ble_ok ? 'OK' : 'Poor'}
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: 'var(--tg-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Ext voltage</div>
                  <div style={{ fontSize: 13 }}>
                    <VoltageGauge mv={sh.ext_voltage_mv} />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: 'var(--tg-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>GSM signal</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <GsmBars value={sh.gsm_signal || 0} />
                    <span style={{ fontSize: 11, color: 'var(--tg-text-secondary)' }}>{sh.gsm_signal || 0}/5</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: 'var(--tg-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Battery (int)</div>
                  <div style={{ fontSize: 13 }}>
                    <VoltageGauge mv={sh.battery_voltage_mv} />
                  </div>
                </div>
              </div>

              {noDevice ? (
                <div style={{ fontSize: 11, color: 'var(--tg-text-muted)', padding: '6px 0' }}>
                  No device assigned — add a valid 15-digit IMEI to start monitoring.
                </div>
              ) : (sh.alerts || []).length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--tg-success-700, #047857)', padding: '6px 0' }}>
                  ✓ All sensors operating normally
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {sh.alerts.map((al, i) => {
                    const col = SEVERITY_COLORS[al.severity] || SEVERITY_COLORS.medium
                    return (
                      <div key={i} style={{ fontSize: 11, padding: '6px 8px', background: col.bg, color: col.fg, border: '1px solid ' + col.border, borderRadius: 4 }}>
                        <strong style={{ textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.05em' }}>{al.severity}</strong> · {al.msg}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {(!data?.trucks || data.trucks.length === 0) && (
        <div className="tg-card" style={{ padding: 32, textAlign: 'center', color: 'var(--tg-text-muted)' }}>
          No trucks in fleet.
        </div>
      )}
    </div>
  )
}
